import type { ReactNode } from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SimpleSelect } from '@/components/ui/select-with-more'
import { StateSelect } from '@/components/ui/StateSelect'
import { Textarea } from '@/components/ui/textarea'
import { AddressField } from './AddressField'
import { AmountsField } from './AmountsField'
import { ChoiceField } from './ChoiceField'
import { DateField } from './DateField'
import type { FieldInputProps } from './fieldInputProps'
import { MonthField } from './MonthField'
import { OptionsField } from './OptionsField'
import { RowsField } from './RowsField'
import {
  type AddressValue,
  type AmountsValue,
  type Answers,
  digitsOnly,
  EARLIEST_YEAR,
  formatEin,
  type RowValue,
  ScreenFieldKind,
} from './screenForm'
import { StatesField } from './StatesField'
import { TradeField } from './TradeField'

interface ScreenFieldItemProps extends Omit<FieldInputProps, 'invalid'> {
  error?: string
  scope: Answers
  renderRow?: (row: RowValue, setRow: (row: RowValue) => void) => ReactNode
}

/** One field: its label, help, control and error. */
export function ScreenFieldItem({ error, scope, renderRow, ...props }: ScreenFieldItemProps) {
  const { field, id } = props
  const invalid = !!error
  const help = field.help && <p className="text-muted-foreground text-xs">{field.help}</p>

  if (field.kind === ScreenFieldKind.CHECKBOX) {
    return (
      <Field className="gap-1.5" data-field={field.key} data-screen-error={invalid || undefined}>
        <div className="flex items-start gap-2">
          <Checkbox
            id={id}
            className="mt-0.5"
            checked={props.value === true}
            onCheckedChange={(checked) => props.onChange(checked === true)}
            disabled={props.disabled}
            aria-invalid={invalid || undefined}
          />
          <div className="flex flex-col gap-1">
            <Label htmlFor={id}>{field.label}</Label>
            {field.help && <p className="text-muted-foreground text-xs whitespace-pre-line">{field.help}</p>}
          </div>
        </div>
        <FieldError message={error} />
      </Field>
    )
  }

  // AddressFields draws its own heading; a field that is the chat's own question has none.
  const label = field.kind !== ScreenFieldKind.ADDRESS && field.label && (
    <FieldLabel htmlFor={id} optional={!field.required}>
      {field.label}
    </FieldLabel>
  )

  return (
    <Field className="gap-1.5" data-field={field.key} data-screen-error={invalid || undefined}>
      {label}
      {field.kind !== ScreenFieldKind.ADDRESS && help}
      <FieldControl {...props} invalid={invalid} scope={scope} renderRow={renderRow} />
      {field.kind === ScreenFieldKind.ADDRESS && help}
      <FieldError message={error} />
    </Field>
  )
}

function FieldControl({
  scope,
  renderRow,
  ...props
}: FieldInputProps & Pick<ScreenFieldItemProps, 'scope' | 'renderRow'>) {
  const { field, value, onChange, id, invalid, disabled } = props
  const text = typeof value === 'string' ? value : ''
  const number = typeof value === 'number' ? value : null
  const list = Array.isArray(value) ? (value as string[]) : []

  switch (field.kind) {
    case ScreenFieldKind.TEXT:
    case ScreenFieldKind.EIN:
    case ScreenFieldKind.DOT:
      return (
        <Input
          id={id}
          value={text}
          onChange={(event) => onChange(normalizeTyped(field.kind, event.target.value))}
          placeholder={field.placeholder || (field.kind === ScreenFieldKind.EIN ? '12-3456789' : undefined)}
          maxLength={field.kind === ScreenFieldKind.TEXT ? field.maxLength || undefined : undefined}
          inputMode={field.kind === ScreenFieldKind.TEXT ? undefined : 'numeric'}
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      )
    case ScreenFieldKind.LONG_TEXT:
      return (
        <Textarea
          id={id}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || undefined}
          maxLength={field.maxLength || undefined}
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      )
    case ScreenFieldKind.NUMBER:
      return (
        <Input
          id={id}
          value={number ?? ''}
          onChange={(event) => {
            const digits = digitsOnly(event.target.value)
            onChange(digits ? Number(digits) : null)
          }}
          placeholder={field.placeholder || undefined}
          inputMode="numeric"
          className="max-w-48"
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      )
    case ScreenFieldKind.MONEY:
      return (
        <CurrencyInput
          id={id}
          value={number}
          onChange={onChange}
          placeholder={field.placeholder || undefined}
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      )
    case ScreenFieldKind.YEAR:
      return (
        <SimpleSelect
          id={id}
          options={yearOptions()}
          value={number === null ? undefined : String(number)}
          onValueChange={(year) => onChange(Number(year))}
          placeholder={field.placeholder || 'Year'}
          error={invalid}
          disabled={disabled}
          className="w-full max-w-48"
        />
      )
    case ScreenFieldKind.SELECT:
      return (
        <SimpleSelect
          id={id}
          options={field.options}
          value={text || undefined}
          onValueChange={onChange}
          placeholder={field.placeholder || 'Select…'}
          error={invalid}
          disabled={disabled}
          className="w-full"
        />
      )
    case ScreenFieldKind.STATE:
      return <StateSelect id={id} value={text} onChange={onChange} error={invalid} disabled={disabled} />
    case ScreenFieldKind.DATE:
      return <DateField {...props} value={text} />
    case ScreenFieldKind.MONTH:
      return <MonthField {...props} value={text} />
    case ScreenFieldKind.CHOICE:
      return <ChoiceField {...props} value={text} />
    case ScreenFieldKind.MULTI:
    case ScreenFieldKind.CARDS:
      return <OptionsField {...props} value={list} />
    case ScreenFieldKind.TRADE:
      return <TradeField {...props} value={text} />
    case ScreenFieldKind.STATES:
      return <StatesField {...props} value={list} />
    case ScreenFieldKind.ADDRESS:
      return <AddressField {...props} value={value as AddressValue} />
    case ScreenFieldKind.AMOUNTS:
      return <AmountsField {...props} value={(value as AmountsValue | null) ?? {}} scope={scope} />
    case ScreenFieldKind.ROWS:
      return renderRow ? (
        <RowsField {...props} value={(value as RowValue[] | null) ?? []} renderRow={renderRow} />
      ) : null
    default:
      return null
  }
}

/** Formats an EIN as it's typed; letters are left in for the server to refuse, never stripped into another number. */
function normalizeTyped(kind: ScreenFieldKind, typed: string): string {
  if (kind === ScreenFieldKind.EIN && /^[\d\s-]*$/.test(typed)) return formatEin(typed)
  return typed
}

function yearOptions() {
  const thisYear = new Date().getFullYear()
  return Array.from({ length: thisYear - EARLIEST_YEAR + 1 }, (_, index) => {
    const year = String(thisYear - index)
    return { value: year, label: year }
  })
}
